import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  Button,
  Typography,
  CardHeader,
  CardActions,
  CardContent,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import api from 'utils/api';
import AddressFormFields from 'components/address/AddressFormFields';
import { emptyAddressFormValues } from 'components/address/mapPlaceToFormValues';
import { formatAddressDisplay } from 'components/address/formatAddressDisplay';
import { isAddressComplete } from 'components/address/addressValidation';
import { DEFAULT_COUNTRY_CODE, getCountryName } from 'components/address/countries';

const formatPhone = (input = '') => {
  const digits = input.replace(/\D/g, '').slice(0, 10);
  if (!digits) return '';
  let formatted = '';
  if (digits.length < 4) formatted = `(${digits}`;
  else if (digits.length < 7) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  else formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return formatted;
};
const isValidPhone = (phone) => /^\(\d{3}\) \d{3}-\d{4}$/.test(phone);

const normalizeAddressList = (data) => (Array.isArray(data) ? data : []);

const mapApiAddressToForm = (address) => ({
  street: address.street || '',
  addressLine2: address.addressLine2 || '',
  city: address.city || '',
  state: address.state || '',
  zipCode: address.zipCode || '',
  countryCode: address.countryCode || DEFAULT_COUNTRY_CODE,
  countryName: address.countryName || getCountryName(address.countryCode || DEFAULT_COUNTRY_CODE),
  latitude: address.latitude ?? null,
  longitude: address.longitude ?? null,
  phone: address.phone ? formatPhone(address.phone) : '',
});

const BusinessAddresses = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [addresses, setAddresses] = useState([]);
  const [newAddress, setNewAddress] = useState({
    ...emptyAddressFormValues(),
    phone: '',
  });
  const [open, setOpen] = useState(false);
  const [currentEditIndex, setCurrentEditIndex] = useState(null);

  const businessId = useSelector(
    (state) => state.business.businessId || state.business.companyInfo?.businessId
  );

  useEffect(() => {
    if (businessId) {
      api
        .get(`/business/${businessId}/addresses`)
        .then((data) => {
          const additionalAddresses = normalizeAddressList(data).filter((a) => a.addressId !== 0);
          setAddresses(additionalAddresses);
        })
        .catch(() => {
          enqueueSnackbar('Error fetching addresses', { variant: 'error' });
        });
    }
  }, [businessId, enqueueSnackbar]);

  const handleClickOpen = (index = null) => {
    if (index !== null) {
      setNewAddress(mapApiAddressToForm(addresses[index]));
      setCurrentEditIndex(index);
    } else {
      setNewAddress({ ...emptyAddressFormValues(), phone: '' });
      setCurrentEditIndex(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setNewAddress({ ...emptyAddressFormValues(), phone: '' });
    setCurrentEditIndex(null);
  };

  const buildPayload = () => {
    const { phone, ...mailing } = newAddress;
    return {
      ...mailing,
      phone: phone || '',
    };
  };

  const handleAddOrEditAddress = () => {
    const isPhoneValid = !newAddress.phone || isValidPhone(newAddress.phone);
    if (!isAddressComplete(newAddress) || !isPhoneValid) {
      if (!isPhoneValid) {
        enqueueSnackbar('Phone must be blank or in format (212) 555-1234', { variant: 'warning' });
      } else {
        enqueueSnackbar('Please fill out all required address fields', { variant: 'warning' });
      }
      return;
    }

    const data = buildPayload();

    if (currentEditIndex !== null) {
      const addressId = addresses[currentEditIndex].addressId;
      api
        .put(`/business/${businessId}/addresses/${addressId}`, data)
        .then(() => api.get(`/business/${businessId}/addresses`))
        .then((addressData) => {
          const additionalAddresses = normalizeAddressList(addressData).filter((a) => a.addressId !== 0);
          setAddresses(additionalAddresses);
          handleClose();
          enqueueSnackbar('Address updated successfully', { variant: 'success' });
        })
        .catch((err) => {
          console.error('Error updating address:', err);
          enqueueSnackbar('Error updating address', { variant: 'error' });
        });
    } else {
      api
        .post(`/business/${businessId}/addresses`, { ...data, businessId })
        .then(() => api.get(`/business/${businessId}/addresses`))
        .then((addressData) => {
          const additionalAddresses = normalizeAddressList(addressData).filter((a) => a.addressId !== 0);
          setAddresses(additionalAddresses);
          handleClose();
          enqueueSnackbar('Address added successfully', { variant: 'success' });
        })
        .catch((err) => {
          console.error('Error adding address:', err);
          enqueueSnackbar('Error adding address', { variant: 'error' });
        });
    }
  };

  const handleDeleteAddress = (index) => {
    const addressId = addresses[index].addressId;
    api
      .delete(`/business/${businessId}/addresses/${addressId}`)
      .then(() => {
        const updated = addresses.filter((_, i) => i !== index);
        setAddresses(updated);
        enqueueSnackbar('Address deleted successfully', { variant: 'success' });
      })
      .catch((err) => {
        if (err?.linkedOffers?.length) {
          const offers = err.linkedOffers || [];
          const message = err.message || 'An error occurred.';
          const offerText = offers.map((o) => o.headline_1).join(', ');
          enqueueSnackbar(`${message} Linked offers: ${offerText}`, { variant: 'error' });
        } else {
          enqueueSnackbar('An error occurred while deleting the address', { variant: 'error' });
        }
      });
  };

  return (
    <Box mb={2}>
      <Card>
        <CardHeader subheader="Additional business addresses" />
        <CardContent
          sx={{
            maxHeight: 200,
            overflowY: 'scroll',
            '::-webkit-scrollbar': { width: 10 },
            '::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '5px',
              '&:hover': { background: '#555' },
            },
            '::-webkit-scrollbar-track': {
              background: '#f0f0f0',
            },
          }}
        >
          {addresses.length > 0 ? (
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Address</TableCell>
                    <TableCell align="center">Phone</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {addresses.map((address, index) => (
                    <TableRow key={address.addressId || index} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatAddressDisplay(address)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {address.phone ? formatPhone(address.phone) : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Button onClick={() => handleClickOpen(index)} color="primary">
                            Edit
                          </Button>
                          <Button onClick={() => handleDeleteAddress(index)} color="error">
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary" variant="body2">
              No Additional addresses added yet.
            </Typography>
          )}
        </CardContent>
        <CardActions>
          <Button color="primary" onClick={() => handleClickOpen()}>
            Add address
          </Button>
        </CardActions>
      </Card>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{currentEditIndex !== null ? 'Edit address' : 'Add a new address'}</DialogTitle>
        <DialogContent>
          <AddressFormFields
            values={newAddress}
            onChange={(patch) => setNewAddress((prev) => ({ ...prev, ...patch }))}
            searchLabel="Search business address"
          />
          <TextField
            fullWidth
            label="Phone (optional)"
            value={newAddress.phone}
            onChange={(e) => setNewAddress({ ...newAddress, phone: formatPhone(e.target.value) })}
            margin="normal"
            error={Boolean(newAddress.phone) && !isValidPhone(newAddress.phone)}
            helperText={
              Boolean(newAddress.phone) && !isValidPhone(newAddress.phone)
                ? 'Phone format: (212) 555-1234'
                : 'A phone number will allow users to call from your offer.'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddOrEditAddress} color="primary">
            {currentEditIndex !== null ? 'Save changes' : 'Save address'}
          </Button>
          <Button onClick={handleClose} color="error">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BusinessAddresses;
